/** `indskr_type` value for a note the voice note-taker produces. */
export const NOTE_TYPE_MEETING_VOICE_NOTE = 1;

/**
 * A voice note as stored on disk.
 *
 * The record keeps its own vocabulary rather than the entity's column names, and
 * the data layer translates at the Dynamics boundary — `rowToRawNote` on the way
 * in, `toNotePayload` on the way out. Two things follow from that. A device
 * upgrading reads the notes it already holds, because the names it stored are the
 * names it still reads. And nothing here begins with an underscore, so PouchDB —
 * which rejects custom `_*` members at the top level — never sees a key it will
 * refuse.
 */
export interface RawVoiceNote {
  indskr_iovoicenoteid: string;
  indskr_name: string | null;
  indskr_transcript: string;
  /** Whether the rep dictated a call note or a customer insight. Device-only. */
  indskr_notetype?: VoiceNoteType;
  /** ISO timestamp of when the dictation was captured. */
  indskr_notedate: string;
  /** The meeting this note became. Set means handled, and it leaves My Notes. */
  indskr_convertedactivityid?: string | null;
  /** The capturing app's own id, which the push resolves a server row by. */
  externalId?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** The appointment this note was captured against, when it arrived filed. */
  targetMeetingId?: string | null;
  targetMeetingName?: string | null;
  /** Who the note is about — an account or contact, independent of the target meeting. */
  customerId?: string | null;
  customerName?: string | null;
  customerType?: string | null;
  pendingPushToDynamics?: boolean;
  isDeleted?: boolean;
}

/** What the rep chose to dictate. Routing only; it stays on the device. */
export enum VoiceNoteType {
  CallNote = 548910000,
  Insight = 548910001,
}

/**
 * The two entity types the customer lookup can target. Dataverse never exposes this as a
 * selectable attribute of its own — `_indskr_customer_value@Microsoft.Dynamics.CRM.lookuplogicalname`
 * is the only place it comes back over FetchXML/OData, verified against a live `dev` row.
 */
export type CustomerType = 'contact' | 'account';

/** Narrows a raw lookup logical name to a {@link CustomerType}, or null for anything else. */
function toCustomerType(logicalName: string | null | undefined): CustomerType | null {
  return logicalName === 'contact' || logicalName === 'account' ? logicalName : null;
}

/** Note title: first non-empty line of the body, capped, or a dated fallback. */
export function deriveNoteTitle(body: string, capturedAt: Date): string {
  const firstLine = (body ?? '').split('\n').map(line => line.trim()).find(Boolean) ?? '';
  if (!firstLine) {
    return `Voice note ${capturedAt.toLocaleDateString()}`;
  }
  return firstLine.length > 80 ? `${firstLine.slice(0, 77)}...` : firstLine;
}

/** App-facing voice-note model consumed by the My Notes UI. */
export class VoiceNote {
  public readonly id: string;
  public readonly name: string;
  public readonly transcript: string;
  public readonly type: VoiceNoteType;
  public readonly noteDate: Date;
  /** The appointment this note is filed against, when it has one. */
  public readonly targetMeetingId: string | null;
  public readonly targetMeetingName: string | null;
  /** Who the note is about, when Dynamics resolved one. Independent of the target meeting. */
  public readonly customerId: string | null;
  public readonly customerName: string | null;
  public readonly customerType: CustomerType | null;
  public readonly location: string | null;
  public readonly latitude: number | null;
  public readonly longitude: number | null;
  /** False once the note has been handled and its record deactivated. */
  public readonly isActive: boolean;

  constructor(raw: RawVoiceNote) {
    this.id = raw.indskr_iovoicenoteid;
    this.transcript = raw.indskr_transcript ?? '';
    this.noteDate = raw.indskr_notedate ? new Date(raw.indskr_notedate) : new Date(0);
    this.name = raw.indskr_name?.trim() || deriveNoteTitle(this.transcript, this.noteDate);
    this.type = raw.indskr_notetype ?? VoiceNoteType.CallNote;
    // A converted note names the meeting it became, which is also the meeting it
    // is filed against; one that merely arrived filed names only the latter.
    this.targetMeetingId = raw.indskr_convertedactivityid ?? raw.targetMeetingId ?? null;
    this.targetMeetingName = raw.targetMeetingName ?? null;
    this.customerId = raw.customerId ?? null;
    this.customerName = raw.customerName ?? null;
    this.customerType = toCustomerType(raw.customerType);
    this.location = raw.location ?? null;
    this.latitude = raw.latitude ?? null;
    this.longitude = raw.longitude ?? null;
    this.isActive = !raw.indskr_convertedactivityid;
  }

  /** A note captured against a meeting that already exists. */
  public get isFiledAgainstMeeting(): boolean {
    return !!this.targetMeetingId;
  }

  /** This note names a customer — a resolved id and a type Dynamics could bind. */
  public get namesCustomer(): boolean {
    return !!this.customerId && !!this.customerType;
  }

  /**
   * Whether the rep may still say who this note was about.
   *
   * **A blank, and only a blank.** The phone may sync a note naming nobody —
   * holding a rep at a lookup they have no signal for costs the note itself —
   * and this is what lets that be put right afterwards. A note that already
   * names somebody was answered where the rep was standing at the time, and one
   * that has become a meeting is finished; neither is ours to edit.
   */
  public get canNameCustomer(): boolean {
    return this.isActive && !this.namesCustomer;
  }
}
